import React, { useEffect, useRef, useState } from 'react';
import { Replace } from 'lucide-react';
import ChangeTemplateModal from './ChangeTemplateModal';
import ordersService, { type ChangeTemplateEligibility } from '../../../services/orders.service';

interface CodeName {
  recno: number;
  name: string;
}

interface Props {
  pronumber: number | string;
  /** Render only when the user can edit LC fields (order-lc-fields gate) — the
   *  same audience the apply endpoint enforces server-side. */
  canEdit: boolean;
  /** Never render on a template — you can't change the template of a template. */
  isTemplate?: boolean;
  /** Seeds the search (V1 changetemplate_lookup prefills these from the pro). */
  billTo: CodeName;
  customer: CodeName;
  moveType?: string;
  /** The pro's current template — excluded from the candidate results. */
  currentTemplate?: string | number;
  /** Called after a successful apply so the container refetches the order. */
  onApplied?: () => void;
}

/**
 * "Change Template" trigger for the Pro Details → Addl Info Template row.
 *
 * Fetches eligibility (V1 changetemplate_validate, order-only checks) and renders
 * an icon+label button that is DISABLED — with the blocking reasons on hover —
 * when the order can't be re-templated (invoiced / product arrived / shipped).
 * An eligible click opens the Change Template modal (search → review).
 */
const ChangeTemplateTrigger: React.FC<Props> = ({
  pronumber,
  canEdit,
  isTemplate = false,
  billTo,
  customer,
  moveType,
  currentTemplate,
  onApplied,
}) => {
  const [elig, setElig] = useState<ChangeTemplateEligibility | null>(null);
  const [open, setOpen] = useState(false);
  const seenPro = useRef<string | null>(null);

  useEffect(() => {
    if (!canEdit || !pronumber || isTemplate) return;
    const ctrl = new AbortController();
    seenPro.current = String(pronumber);
    ordersService
      .changeTemplateEligibility(pronumber, ctrl.signal)
      .then(setElig)
      .catch(() => {
        /* leave null → trigger stays disabled ("checking…") */
      });
    return () => ctrl.abort();
  }, [pronumber, canEdit, isTemplate]);

  // Never on a template, and only for users who can edit LC fields.
  if (isTemplate || !canEdit) return null;

  const eligible = elig?.eligible === true;
  const reasons = elig?.reasons ?? [];
  const title = eligible
    ? 'Change template'
    : reasons.length
      ? `Change Template unavailable:\n• ${reasons.join('\n• ')}`
      : 'Checking template-change availability…';

  return (
    <>
      <button
        type="button"
        disabled={!eligible}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 h-6 px-1.5 shrink-0 rounded text-[0.6875rem] font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:text-slate-300 dark:disabled:text-gray-600 disabled:hover:bg-transparent disabled:cursor-not-allowed"
        title={title}
        aria-label={title}
      >
        <Replace className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
        Change Template
      </button>
      {open && (
        <ChangeTemplateModal
          isOpen={open}
          pronumber={pronumber}
          initialBillTo={billTo}
          initialCustomer={customer}
          moveType={moveType}
          currentTemplate={currentTemplate}
          lockedDelivery={elig?.deliveryLock ?? null}
          onClose={() => setOpen(false)}
          onApplied={onApplied}
        />
      )}
    </>
  );
};

export default ChangeTemplateTrigger;
